import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary, useTheme } from '@mui/material';
import { Button } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { CloseIcon } from '@icons/CloseIcon';
import { ErrorIcon } from '@icons/ErrorIcon';
import { ListIcon } from '@icons/ListIcon';
import { WizardIcon } from '@icons/WizardIcon';
import { useGetWorkflowBaseTrigger } from '@queryHooks/useWorkflowBaseTrigger';
import type { SubscriptionListModel } from '@models/SubscriptionListModel';
import type { SoftwareSimpleModel } from '@models/softwareModels';
import type { WorkflowBaseTriggerFieldDTOModel } from '@repositories/workflowBaseTriggerRepository/models/WorkflowBaseTriggerFieldDTOModel';
import { ProductAutocomplete } from '../../createAndDetail/ProductAutocomplete';
import SubscriptionAutocomplete from '../../createAndDetail/SubscriptionAutocomplete';

interface Props {
  triggerId: string | undefined;
  paramResourceType?: string; // InputParameter의 resourceType (connector에서 정의)
  paramName?: string; // InputParameter의 name (subscriptionId 또는 productId)
  onClick: (type: 'action' | 'trigger', field: string, displayName: string, idx: number) => void;
  onResourceConfirm?: (
    spelExpression: string | null,
    resourceId: string | null,
    resourceName: string | null,
    idx: number,
  ) => void;
  onClose: () => void;
  index: number;
}

export const InputDataPopup = ({ triggerId, paramResourceType, onClick, onResourceConfirm, onClose, index }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: triggerDetail } = useGetWorkflowBaseTrigger(triggerId || '', { enabled: !!triggerId });

  // 구독 선택 상태
  const [subscriptionExpanded, setSubscriptionExpanded] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('');
  const [selectedSubscriptionName, setSelectedSubscriptionName] = useState<string>('');

  // 제품 선택 상태
  const [productExpanded, setProductExpanded] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedProductName, setSelectedProductName] = useState<string>('');

  const isSubscriptionResourceType = paramResourceType === 'subscription';

  const handleClickInputData = (input: WorkflowBaseTriggerFieldDTOModel) => {
    onClick('trigger', input.name, input.displayName || input.name, index);
  };

  const handleSubscriptionChange = (subscription: SubscriptionListModel) => {
    setSelectedSubscriptionId(subscription.id);
    setSelectedSubscriptionName(subscription.name);
  };

  const handleProductChange = (product: SoftwareSimpleModel | null) => {
    setSelectedProductId(product?.id ?? '');
    setSelectedProductName(product?.name ?? '');
  };

  const handleSubscriptionConfirm = () => {
    onResourceConfirm?.(null, selectedSubscriptionId, selectedSubscriptionName, index);
    setSubscriptionExpanded(false);
    setSelectedSubscriptionId('');
    setSelectedSubscriptionName('');
  };

  const handleProductConfirm = () => {
    onResourceConfirm?.(null, selectedProductId, selectedProductName, index);
    setProductExpanded(false);
    setSelectedProductId('');
    setSelectedProductName('');
  };

  return createPortal(
    <Container>
      <Header>
        <Title>{t('Input_Data_Popup_01')}</Title>
        <Button variant="text" onClick={onClose}>
          <CloseIcon width={24} height={24} />
        </Button>
      </Header>
      <Body>
        {/* 트리거 아코디언 */}
        {!!triggerDetail?.fields.length && (
          <InputData disableGutters defaultExpanded>
            <DataTitleWrapper
              expandIcon={<ChevronIcon rotateNum={180} width={20} height={20} color={theme.colors['ic-gray-light']} />}
            >
              <DataTitle>
                <WizardIcon width={16} height={16} />
                <span>{t('Workflow_Create_02')}</span>
              </DataTitle>
            </DataTitleWrapper>
            <DataItems>
              {triggerDetail?.fields?.map(field => (
                <DataItem key={field.name} onClick={() => handleClickInputData(field)}>
                  {field.displayName}
                </DataItem>
              ))}
            </DataItems>
          </InputData>
        )}

        {/* resourceType이 subscription인 경우 현재 편집 중인 필드에 맞는 리소스 선택만 표시 */}
        {/* 구독 ID 필드 편집 시: 구독 목록만 */}
        {isSubscriptionResourceType && (
          <ResourceSelectAccordion
            disableGutters
            expanded={subscriptionExpanded}
            onChange={(_, isExpanded) => setSubscriptionExpanded(isExpanded)}
          >
            <ResourceSelectSummary
              expandIcon={<ChevronIcon rotateNum={180} width={16} height={16} color={theme.colors['ic-gray-light']} />}
            >
              <ListIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
              <span>{t('Workflow.action.subscription_select')}</span>
            </ResourceSelectSummary>
            <ResourceSelectDetails>
              <SubscriptionAutocomplete value={selectedSubscriptionId} onChange={handleSubscriptionChange} isEditable />
              <ConfirmButtonWrapper>
                <ConfirmButton
                  variant="contain"
                  size="small"
                  onClick={handleSubscriptionConfirm}
                  disabled={!selectedSubscriptionId}
                >
                  {t('Common.Confirm')}
                </ConfirmButton>
              </ConfirmButtonWrapper>
            </ResourceSelectDetails>
          </ResourceSelectAccordion>
        )}

        {/* 제품 ID 필드 편집 시: 제품 목록만 */}
        {isSubscriptionResourceType && (
          <ResourceSelectAccordion
            disableGutters
            expanded={productExpanded}
            onChange={(_, isExpanded) => setProductExpanded(isExpanded)}
          >
            <ResourceSelectSummary
              expandIcon={<ChevronIcon rotateNum={180} width={16} height={16} color={theme.colors['ic-gray-light']} />}
            >
              <ListIcon width={16} height={16} color={theme.colors['ic-gray-main']} />
              <span>{t('Workflow.action.product_select')}</span>
            </ResourceSelectSummary>
            <ResourceSelectDetails>
              <ProductAutocomplete value={selectedProductId} onChange={handleProductChange} isEditable />
              <ConfirmButtonWrapper>
                <ConfirmButton
                  variant="contain"
                  size="small"
                  onClick={handleProductConfirm}
                  disabled={!selectedProductId}
                >
                  {t('Common.Confirm')}
                </ConfirmButton>
              </ConfirmButtonWrapper>
            </ResourceSelectDetails>
          </ResourceSelectAccordion>
        )}

        {!triggerDetail?.fields.length && !isSubscriptionResourceType && (
          <Empty>
            <EmptyCircle>
              <ErrorIcon color={theme.colors['ic-primary']} />
            </EmptyCircle>
            <EmptyInfo>
              {t('Workflow_Create_34')}
              <EmptySubInfo>{t('Workflow_Create_35')}</EmptySubInfo>
            </EmptyInfo>
          </Empty>
        )}
      </Body>
    </Container>,
    document.body,
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 100;
  bottom: 8px;
  right: 768px;
  transform: translate(calc(50% - 73px), 0);
  width: 400px;
  height: 640px;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: 0px 8px 10px -5px #00000014, 0px 16px 24px 2px #0000000a, 0px 6px 30px 5px #0000000d;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 60px;
  align-items: center;
  justify-content: space-between;
  padding: 0px 18px 0px 28px;
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  border-bottom: 1px solid ${({ theme }) => theme.colors['border-gray-w-lighter']};
`;

const Title = styled.strong`
  ${fonts.Headline6}
`;

const Body = styled.div`
  padding: 20px 28px;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 10px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-lighter']};
`;

const ResourceSelectAccordion = styled(Accordion)`
  box-shadow: none;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 4px;

  &:before {
    display: none;
  }

  &.Mui-expanded {
    margin: 0;
  }
`;

const ResourceSelectSummary = styled(AccordionSummary)`
  min-height: 40px;
  padding: 0 16px;

  & .MuiAccordionSummary-content {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    ${fonts.Body2};
  }
`;

const ResourceSelectDetails = styled(AccordionDetails)`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: ${props => props.theme.colors['bg-gray-light-50']};
`;

const ConfirmButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button)`
  padding: 0px 16px;
`;

const InputData = styled(Accordion)`
  box-shadow: none;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 4px;
`;

const DataTitleWrapper = styled(AccordionSummary)`
  height: 40px;
`;

const DataTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  ${fonts.Headline8};
  align-items: center;
  padding: 0px 4px;
`;

const DataItems = styled(AccordionDetails)`
  padding: 0px;
`;

const DataItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0px 20px;
  ${fonts.Body2};
  background-color: ${props => props.theme.colors['bg-gray-light-50']};
  border: 1px solid ${props => props.theme.colors['bg-gray-light-50']};
  cursor: pointer;

  :hover {
    background-color: ${props => props.theme.colors['state-white-primary-hover']};
    border: 1px solid ${props => props.theme.colors['border-primary-light']};
  }
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const EmptyCircle = styled.div`
  display: flex;
  width: 36px;
  height: 36px;
  border-radius: 100%;
  background-color: ${props => props.theme.colors['bg-primary-lighter']};
  align-items: center;
  justify-content: center;
`;

const EmptyInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${fonts.Body2};
`;

const EmptySubInfo = styled.div`
  ${fonts.Body4};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;
